import React from 'react';

import { useFormContext } from 'react-hook-form';
import { Collapse, Grid, InputAdornment } from '@mui/material';

import { DispatchEventReport } from '../../../core/api/clients';

import ControlledCheckbox from '../../../components/ControlledCheckbox';
import ControlledTextField from '../../../components/ControlledTextField';
import ControlledSelect from '../../../components/ControlledSelect';
import ControlledRadioGroup from '../../../components/ControlledRadioGroup';

import { useDispatchEventReportData } from '../../../core/context/DispatchEventReportDataContext';
import useWatchAndSetValue from '../../../core/hooks/useWatchAndSetValue';

function WeatherStep({ visible, readOnly }: { visible: boolean, readOnly?: boolean }) {
  const { control } = useFormContext<DispatchEventReport>();
  const { allowedActions } = useDispatchEventReportData();

  useWatchAndSetValue<DispatchEventReport>({
    watchedFieldName: 'eventType3',
    fieldToSet: 'windShear',
    valueToSet: true,
    watchedFieldCondition: (watchedFieldValue) => watchedFieldValue === 'Windshear Encounter',
  });

  useWatchAndSetValue<DispatchEventReport>({
    watchedFieldName: 'eventType3',
    fieldToSet: 'lightning',
    valueToSet: true,
    watchedFieldCondition: (watchedFieldValue) => watchedFieldValue === 'Lightning Strike',
  });

  const padWindDirection = (e: any) => {
    const { value } = e.target;
    if (value === '') return '';
    return (`00${value}`).slice(-3);
  };

  return (
    <Collapse in={visible}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledSelect<DispatchEventReport>
            name="timeOfDay"
            control={control}
            readOnly={readOnly}
            menuItems={[
              { name: 'Sunrise', value: 'Sunrise' },
              { name: 'Day', value: 'Day' },
              { name: 'Sunset', value: 'Sunset' },
              { name: 'Night', value: 'Night' },
            ]}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledTextField<DispatchEventReport>
            name="windDirection"
            control={control}
            readOnly={readOnly}
            TextFieldProps={{
              InputProps: {
                endAdornment: <InputAdornment position="end">degrees</InputAdornment>,
              },
            }}
            transform={padWindDirection}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledTextField<DispatchEventReport>
            control={control}
            name="windSpeed"
            TextFieldProps={{
              InputProps: {
                endAdornment: <InputAdornment position="end">kts</InputAdornment>,
              },
            }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledTextField<DispatchEventReport>
            control={control}
            name="windGusts"
            TextFieldProps={{
              InputProps: {
                endAdornment: <InputAdornment position="end">kts</InputAdornment>,
              },
            }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledSelect<DispatchEventReport>
            control={control}
            name="precipitation"
            menuItems={[
              { name: 'Drizzle', value: 'Drizzle' },
              { name: 'Light Rain', value: 'Light Rain' },
              { name: 'Moderate Rain', value: 'Moderate Rain' },
              { name: 'Heavy Rain', value: 'Heavy Rain' },
              { name: 'Thunderstorms', value: 'Thunderstorms' },
              { name: 'Hail', value: 'Hail' },
              { name: 'Freezing Rain', value: 'Freezing Rain' },
              { name: 'Sleet/Ice Pellets', value: 'Sleet/Ice Pellets' },
              { name: 'Light Snow', value: 'Light Snow' },
              { name: 'Moderate Snow', value: 'Moderate Snow' },
              { name: 'Heavy Snow', value: 'Heavy Snow' },
              { name: 'N/A', value: 'N/A' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledSelect<DispatchEventReport>
            control={control}
            name="visibility"
            menuItems={[
              { name: '1/8', value: '1/8' },
              { name: '1/4', value: '1/4' },
              { name: '1/2', value: '1/2' },
              { name: '3/4', value: '3/4' },
              { name: '1', value: '1' },
              { name: '1 1/4', value: '1 1/4' },
              { name: '1 1/2', value: '1 1/2' },
              { name: '1 3/4', value: '1 3/4' },
              { name: '2', value: '2' },
              { name: '2 1/4', value: '2 1/4' },
              { name: '2 1/2', value: '2 1/2' },
              { name: '2 3/4', value: '2 3/4' },
              { name: '3', value: '3' },
              { name: '3 1/4', value: '3 1/4' },
              { name: '3 1/2', value: '3 1/2' },
              { name: '3 3/4', value: '3 3/4' },
              { name: '4', value: '4' },
              { name: '4 1/4', value: '4 1/4' },
              { name: '4 1/2', value: '4 1/2' },
              { name: '4 3/4', value: '4 3/4' },
              { name: '5', value: '5' },
              { name: '5 1/4', value: '5 1/4' },
              { name: '5 1/2', value: '5 1/2' },
              { name: '5 3/4', value: '5 3/4' },
              { name: '6', value: '6' },
              { name: '6 1/4', value: '6 1/4' },
              { name: '6 1/2', value: '6 1/2' },
              { name: '6 3/4', value: '6 3/4' },
              { name: '7', value: '7' },
              { name: '7 1/4', value: '7 1/4' },
              { name: '7 1/2', value: '7 1/2' },
              { name: '7 3/4', value: '7 3/4' },
              { name: '8', value: '8' },
              { name: '8 1/4', value: '8 1/4' },
              { name: '8 1/2', value: '8 1/2' },
              { name: '8 3/4', value: '8 3/4' },
              { name: '9', value: '9' },
              { name: '9 1/4', value: '9 1/4' },
              { name: '9 1/2', value: '9 1/2' },
              { name: '9 3/4', value: '9 3/4' },
              { name: '10+', value: '10+' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledSelect<DispatchEventReport>
            control={control}
            label="RVR"
            name="rvr"
            menuItems={[
              { name: '<500', value: '<500' },
              { name: '500', value: '500' },
              { name: '600', value: '600' },
              { name: '700', value: '700' },
              { name: '800', value: '800' },
              { name: '900', value: '900' },
              { name: '1000', value: '1000' },
              { name: '1100', value: '1100' },
              { name: '1200', value: '1200' },
              { name: '1300', value: '1300' },
              { name: '1400', value: '1400' },
              { name: '1500', value: '1500' },
              { name: '1600', value: '1600' },
              { name: '1700', value: '1700' },
              { name: '1800', value: '1800' },
              { name: '1900', value: '1900' },
              { name: '2000', value: '2000' },
              { name: '2100', value: '2100' },
              { name: '2200', value: '2200' },
              { name: '2300', value: '2300' },
              { name: '2400', value: '2400' },
              { name: '2500', value: '2500' },
              { name: '2600', value: '2600' },
              { name: '2700', value: '2700' },
              { name: '2800', value: '2800' },
              { name: '2900', value: '2900' },
              { name: '3000', value: '3000' },
              { name: '3100', value: '3100' },
              { name: '3200', value: '3200' },
              { name: '3300', value: '3300' },
              { name: '3400', value: '3400' },
              { name: '3500', value: '3500' },
              { name: '3600', value: '3600' },
              { name: '3700', value: '3700' },
              { name: '3800', value: '3800' },
              { name: '3900', value: '3900' },
              { name: '4000', value: '4000' },
              { name: '4100', value: '4100' },
              { name: '4200', value: '4200' },
              { name: '4300', value: '4300' },
              { name: '4400', value: '4400' },
              { name: '4500', value: '4500' },
              { name: '4600', value: '4600' },
              { name: '4700', value: '4700' },
              { name: '4800', value: '4800' },
              { name: '4900', value: '4900' },
              { name: '5000', value: '5000' },
              { name: '5100', value: '5100' },
              { name: '5200', value: '5200' },
              { name: '5300', value: '5300' },
              { name: '5400', value: '5400' },
              { name: '5500', value: '5500' },
              { name: '5600', value: '5600' },
              { name: '5700', value: '5700' },
              { name: '5800', value: '5800' },
              { name: '5900', value: '5900' },
              { name: '6000', value: '6000' },
              { name: '6100', value: '6100' },
              { name: '6200', value: '6200' },
              { name: '6300', value: '6300' },
              { name: '6400', value: '6400' },
              { name: '6500', value: '6500' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledTextField<DispatchEventReport>
            control={control}
            name="cloudCeiling"
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledRadioGroup<DispatchEventReport>
            RadioGroupProps={{ row: true }}
            name="meteorologicalCondition"
            control={control}
            radioOptions={[
              { value: 'IMC', label: 'IMC' },
              { value: 'VMC', label: 'VMC' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledTextField<DispatchEventReport>
            control={control}
            name="temperature"
            TextFieldProps={{
              InputProps: {
                endAdornment: <InputAdornment position="end">celsius</InputAdornment>,
              },
            }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledCheckbox<DispatchEventReport>
            name="windShear"
            label="Wind Shear"
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledCheckbox<DispatchEventReport>
            name="lightning"
            label="Lightning"
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
    </Collapse>
  );
}

export default WeatherStep;
